import { AxiosError } from 'axios'
import {
  type QueryFunctionContext,
  type QueryObserverOptions,
  useQuery
} from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { grafanaQueryKeys } from 'queries/grafana/query-keys'
import { getEndpointWithParams } from 'queries/queries-utils'

import type { GrafanaMonitorsResponse } from 'types/api/grafana/monitors'

async function fetchGrafanaMonitors({
  queryKey
}: QueryFunctionContext<ReturnType<typeof grafanaQueryKeys.getMonitors>>) {
  const endpointWithParams = getEndpointWithParams(...queryKey)
  const response = await agent.get<GrafanaMonitorsResponse>(endpointWithParams)

  if (response.data.status === 'ERROR') {
    const errorMessageSplit = response.data.error_message?.split(': ') ?? []
    const responseCode = errorMessageSplit[1]
      ? parseInt(errorMessageSplit[1])
      : '400'

    throw new AxiosError(
      response.data.error_message ?? 'Grafana monitor fetch failed',
      responseCode.toString(),
      response.config,
      response.request,
      response
    )
  }

  return response.data
}

export function useGetGrafanaMonitors(
  params: {
    grafanaIntegrationName: string
    searchString: string
  },
  queryOptions: Omit<
    QueryObserverOptions<GrafanaMonitorsResponse, AxiosError>,
    'queryFn' | 'queryKey'
  >
) {
  const { currentTeamId } = useAuth()

  return useQuery<GrafanaMonitorsResponse, AxiosError>({
    ...queryOptions,
    queryFn: fetchGrafanaMonitors,
    queryKey: grafanaQueryKeys.getMonitors({
      ...params,
      teamId: currentTeamId
    })
  })
}
